import React, { useMemo, useState, useCallback, useContext } from 'react';
import classNames from 'classnames';
import {
  ValidatedBootstrapPanel,
  Collapse,
  ValidatedPanel,
  TextTitle,
  useCollapseState,
  getPanelErrorProps,
  collapseExpander,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { Panel as BootstrapPanel } from 'react-bootstrap';
import { Separator } from '@fluentui/react';
import { ValidatedUrlPath, ValidatedMobileUrl, getAdvancedUrlOptionsCardSummaryDetails } from '@bingads-webui-campaign-react/validated-url-input';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { entityIs, andComp, orComp, propertyIs } from '@bingads-webui-campaign-react/form-error';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ValidatedUrlRules, UrlOptionsDetailsWithRules } from '@bingads-webui-campaign-react/url-rules';
import { ValidatedUrlRulesV2, UrlOptionsDetailsWithRulesV2 } from '@bingads-webui-campaign-react/url-rules-v2';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { ERROR_ENTITY } from '../../../shared/model/api-error';

const entityIsAssetGroup = entityIs(ERROR_ENTITY.AssetGroup);
const assetGroupPath1AndPath2Props = andComp(entityIsAssetGroup, orComp(propertyIs('Path1'), propertyIs('Path2')));
const assetGroupMobileFinalUrlsProp = andComp(entityIsAssetGroup, propertyIs('MobileFinalUrls'));

const ServerValidatedUrlPath = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedUrlPath, { filter: assetGroupPath1AndPath2Props }));
const ServerValidatedMobileUrl = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedMobileUrl, { filter: assetGroupMobileFinalUrlsProp }));

const urlOptionStyles = () => ({
  urlContainer: {
    '& .path2, .path1': {
      width: '150px',
    },
  },
});

export const MoreOptionsSection = ({
  permissions,
  finalUrls,
  urlPath1,
  urlPath2,
  finalMobileUrls,
  urlRules,
  urlRulesOption,
  classes,
}) => {
  const i18n = useContext(I18nContext);

  const urlClasses = useDefaultStyles(urlOptionStyles);

  const {
    AssetGroupUrlRules: isAssetGroupUrlRulesEnabled,
    AssetGroupUrlRulesV2: isAssetGroupUrlRulesV2Enabled,
  } = permissions.dynamic;

  const [
    allMoreOptionsSections,
    moreOptionsExpandedSections,
    setMoreOptionsExpandedSections,
  ] = useCollapseState(
    ['advanced-url-options'] /* all sections */,
    ['advanced-url-options'] /* initially expanded sections */
  );

  const [moreOptionsSectionExpanded, setNameAndTitleExpanded] = useState(false);
  const onMoreOptionsSectionToggle = useCallback((expanded) => {
    setNameAndTitleExpanded(expanded);
  }, []);


  const moreOptionsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setMoreOptionsExpandedSections }),
    [setMoreOptionsExpandedSections]
  );

  const moreOptionsSection = useMemo(() => (
    <ValidatedBootstrapPanel
      onToggle={onMoreOptionsSectionToggle}
      expanded={moreOptionsSectionExpanded}
      // defaultExpanded={false}
      className={classNames(classes.collapsiblePanel, 'more-asset-group-options')}
      render={() => (
        <>
          <BootstrapPanel.Heading>
            <BootstrapPanel.Title toggle>
              {i18n.getString(_TL_('More options'))}
            </BootstrapPanel.Title>
          </BootstrapPanel.Heading>
          <BootstrapPanel.Collapse>
            <BootstrapPanel.Body>
              <div className={classNames('asset-group-more-options-section', urlClasses.urlContainer)}>
                <AttentionBeaconGroup>
                  <Collapse allPanelItems={allMoreOptionsSections} checkItems skipItemsTypeCheck activeKeys={moreOptionsExpandedSections} setActiveKeys={setMoreOptionsExpandedSections}>
                    <ValidatedPanel
                      key="advanced-url-options"
                      title={<TextTitle
                        titleKey={_TL_('Advanced URL options')}
                        // eslint-disable-next-line no-nested-ternary
                        details={!isAssetGroupUrlRulesEnabled
                          ? getAdvancedUrlOptionsCardSummaryDetails(i18n, finalUrls, urlPath1, urlPath2, finalMobileUrls)
                          : isAssetGroupUrlRulesV2Enabled
                            ? (<UrlOptionsDetailsWithRulesV2 i18n={i18n} finalUrls={finalUrls} path1={urlPath1} path2={urlPath2} mobileFinalUrls={finalMobileUrls} urlRules={urlRules} />)
                            : (<UrlOptionsDetailsWithRules i18n={i18n} finalUrls={finalUrls} path1={urlPath1} path2={urlPath2} mobileFinalUrls={finalMobileUrls} urlRules={urlRules} />)}
                      />}
                      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                      actuationProps={{
                        expand: moreOptionsExpand,
                        expandKey: 'advanced-url-options',
                      }}
                    >
                      <ServerValidatedUrlPath
                        i18n={i18n}
                        validationProps={{ data: { Path1: urlPath1, Path2: urlPath2, FinalUrls: finalUrls } }}
                      />
                      <ServerValidatedMobileUrl
                        i18n={i18n}
                        validationProps={{ data: { MobileFinalUrls: finalMobileUrls } }}
                      />
                      {
                        isAssetGroupUrlRulesEnabled && !isAssetGroupUrlRulesV2Enabled && (
                          <>
                            <Separator />
                            <ValidatedUrlRules
                              i18n={i18n}
                              validationProps={{ data: { urlRules, urlRulesOption } }}
                            />
                          </>
                        )
                      }
                      {
                        isAssetGroupUrlRulesV2Enabled && (
                          <>
                            <Separator />
                            <ValidatedUrlRulesV2
                              i18n={i18n}
                              validationProps={{ data: { urlRules } }}
                            />
                          </>
                        )
                      }
                    </ValidatedPanel>
                  </Collapse>
                </AttentionBeaconGroup>
              </div>
            </BootstrapPanel.Body>
          </BootstrapPanel.Collapse>
        </>
      )}
    />
  ), [onMoreOptionsSectionToggle, moreOptionsSectionExpanded, classes.collapsiblePanel, i18n, urlClasses.urlContainer, allMoreOptionsSections, moreOptionsExpandedSections, setMoreOptionsExpandedSections, isAssetGroupUrlRulesEnabled, finalUrls, urlPath1, urlPath2, finalMobileUrls, urlRules, moreOptionsExpand, urlRulesOption]);

  return moreOptionsSection;
};
